// /investigations/:slug — detalle con tabs.

import React, { useEffect, useState } from 'react';
import friendlyError from '../../utils/friendlyError';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  FaArrowLeft,
  FaPlus,
  FaTrashAlt,
  FaUserCircle,
  FaBuilding,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaLightbulb,
  FaIndustry,
  FaBook,
  FaFileAlt,
  FaTimes,
} from 'react-icons/fa';
import {
  TechShell,
  TechPageHeader,
  TechSection,
  TechTabs,
  TechButton,
  TechInput,
  TechChip,
  Empty,
  Loading,
  ErrorBox,
  borderSoft,
  panelDeep,
  mono,
  accent,
} from '../../styles/techComponents';
import {
  getInvestigation,
  createEntity, deleteEntity,
  createConnection, deleteConnection,
  createReport, deleteReport, getReport,
  createTheory, getTheory, deleteTheory,
  createNote,
  runAgent,
  getTheoryChatHistory, sendTheoryChat, clearTheoryChat,
} from '../../services/investigationService';
import AgentLauncher from './AgentLauncher';
import EntityGraph from './EntityGraph';
import AutopilotPanel from './AutopilotPanel';
import { FaRobot, FaExternalLinkAlt, FaPaperPlane, FaUser, FaCommentDots } from 'react-icons/fa';
import { wrapWithCitations, CitePopup } from '../UI/Citations';

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-disabled);
  font-family: ${mono};
  font-size: 0.7rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  text-decoration: none;
  margin-bottom: 0.85rem;
  &:hover { color: ${accent}; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 0.75rem;
`;

const EntityCard = styled.div`
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 8px;
  padding: 0.85rem 0.95rem;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;

  .top {
    display: flex;
    align-items: center;
    gap: 0.6rem;
  }
  .avatar {
    width: 36px; height: 36px;
    border-radius: 50%;
    background: rgba(0, 180, 216, 0.1);
    border: 1px solid rgba(0, 180, 216, 0.3);
    color: ${accent};
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }
  .name { color: var(--text-primary); font-weight: 600; font-size: 0.93rem; }
  .type {
    font-family: ${mono};
    font-size: 0.6rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--text-disabled);
  }
  .summary {
    color: var(--text-secondary);
    font-size: 0.83rem;
    line-height: 1.5;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .actions {
    position: absolute;
    top: 8px;
    right: 8px;
    display: flex;
    gap: 4px;
    opacity: 0;
    transition: opacity 0.18s ease;
  }
  &:hover .actions { opacity: 1; }
  .actions button {
    background: rgba(11, 19, 32, 0.7);
    border: 1px solid ${borderSoft};
    color: var(--text-disabled);
    width: 24px; height: 24px;
    border-radius: 5px;
    cursor: pointer;
    font-size: 0.7rem;
    &:hover { color: var(--alert-error); }
  }
`;

const ConnectionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.55rem 0.75rem;
  background: rgba(11, 19, 32, 0.5);
  border: 1px solid ${borderSoft};
  border-radius: 6px;
  margin-bottom: 0.45rem;
  font-size: 0.85rem;

  .from, .to { color: var(--text-primary); font-weight: 600; }
  .kind {
    font-family: ${mono};
    font-size: 0.62rem;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: ${accent};
    background: rgba(0, 180, 216, 0.08);
    border: 1px solid rgba(0, 180, 216, 0.25);
    padding: 2px 7px;
    border-radius: 4px;
  }
  .arrow { color: var(--text-disabled); }
  .desc { color: var(--text-secondary); flex: 1; }
  .del {
    background: transparent;
    border: 1px solid ${borderSoft};
    color: var(--text-disabled);
    width: 26px; height: 26px;
    border-radius: 5px;
    cursor: pointer;
    &:hover { color: var(--alert-error); }
  }
`;

const ReportCard = styled.div`
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 8px;
  padding: 1rem 1.1rem;
  margin-bottom: 0.75rem;
  cursor: pointer;
  transition: all 0.18s ease;
  &:hover { border-color: rgba(0, 180, 216, 0.4); background: rgba(0, 180, 216, 0.04); }

  .title { color: var(--text-primary); font-weight: 600; font-size: 0.96rem; margin-bottom: 5px; }
  .meta {
    font-family: ${mono}; font-size: 0.6rem; letter-spacing: 0.16em;
    text-transform: uppercase; color: var(--text-disabled);
  }
  .preview {
    color: var(--text-secondary);
    font-size: 0.84rem;
    line-height: 1.5;
    margin-top: 0.55rem;
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

const ModalBackdrop = styled.div`
  position: fixed; inset: 0; z-index: 1400;
  background: rgba(8, 14, 24, 0.7); backdrop-filter: blur(3px);
  display: flex; align-items: center; justify-content: center; padding: 1rem;
`;
const ModalCard = styled.div`
  width: min(720px, 100%); max-height: 88vh; overflow-y: auto;
  background: var(--color-primary);
  border: 1px solid rgba(0, 180, 216, 0.35);
  border-radius: 12px;
  padding: 1.3rem 1.4rem;
  position: relative;
  color: var(--text-secondary);

  h2 { color: var(--text-primary); margin: 0 0 1rem; }
  label {
    display: block; font-family: ${mono};
    font-size: 0.62rem; letter-spacing: 0.18em; text-transform: uppercase;
    color: var(--text-disabled); margin: 0.65rem 0 0.3rem;
  }
  textarea {
    width: 100%; min-height: 90px;
    background: rgba(11, 19, 32, 0.65);
    border: 1px solid ${borderSoft};
    border-radius: 7px;
    color: var(--text-primary);
    font-family: inherit;
    padding: 0.55rem 0.75rem;
    font-size: 0.88rem;
    outline: none;
    resize: vertical;
    &:focus { border-color: rgba(0, 180, 216, 0.5); }
  }
  select {
    width: 100%;
    background: rgba(11, 19, 32, 0.65);
    border: 1px solid ${borderSoft};
    border-radius: 7px;
    color: var(--text-primary);
    padding: 0.55rem 0.75rem;
    font-size: 0.88rem;
  }
  .close {
    position: absolute; top: 12px; right: 14px;
    background: transparent; border: 1px solid ${borderSoft};
    color: var(--text-disabled);
    width: 28px; height: 28px; border-radius: 6px; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    &:hover { color: var(--alert-error); }
  }
  .actions { display: flex; justify-content: flex-end; gap: 0.5rem; margin-top: 1rem; }
  .md {
    background: rgba(11, 19, 32, 0.5);
    border: 1px solid ${borderSoft};
    border-radius: 8px;
    padding: 1rem;
    font-size: 0.92rem;
    line-height: 1.65;
    color: var(--text-secondary);
    a { color: ${accent}; }
    h1, h2, h3, h4 { color: var(--text-primary); }
    table { border-collapse: collapse; margin: 0.5rem 0; font-size: 0.86rem; }
    th, td { border: 1px solid ${borderSoft}; padding: 4px 8px; }
    th { color: var(--text-primary); background: rgba(0, 180, 216, 0.06); }
    code { background: rgba(0,180,216,0.1); color: ${accent}; padding: 1px 5px; border-radius: 3px; }
  }
`;

const TheoryChatPanel = styled.div`
  margin-top: 1.2rem;
  padding-top: 1rem;
  border-top: 1px dashed ${borderSoft};
  display: flex;
  flex-direction: column;
  gap: 0.6rem;

  .chat-head {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    .title {
      flex: 1;
      font-family: ${mono};
      font-size: 0.72rem;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      color: var(--text-secondary);
    }
    .clear {
      background: transparent;
      border: 1px solid ${borderSoft};
      color: var(--text-disabled);
      padding: 4px 8px;
      border-radius: 5px;
      cursor: pointer;
      font-size: 0.72rem;
      &:hover { color: var(--alert-error); border-color: rgba(239, 71, 111, 0.4); }
    }
  }
  .chat-body {
    max-height: 360px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    padding: 0.55rem 0.2rem;
    background: rgba(11, 19, 32, 0.35);
    border: 1px solid ${borderSoft};
    border-radius: 8px;
  }
  .empty {
    color: var(--text-disabled);
    font-size: 0.84rem;
    line-height: 1.55;
    padding: 0.4rem 0.6rem;
  }
  .suggest {
    background: transparent;
    border: 1px solid ${borderSoft};
    color: var(--text-secondary);
    text-align: left;
    padding: 6px 10px;
    border-radius: 6px;
    font-size: 0.78rem;
    cursor: pointer;
    &:hover { border-color: rgba(0, 180, 216, 0.4); background: rgba(0, 180, 216, 0.04); }
  }
  .msg {
    display: flex;
    gap: 0.55rem;
    padding: 0 0.3rem;
    &.user .avatar { color: var(--text-disabled); }
    &.assistant .avatar { color: ${accent}; }
    .avatar {
      flex-shrink: 0;
      width: 26px; height: 26px;
      display: flex; align-items: center; justify-content: center;
      border-radius: 50%;
      background: rgba(11, 19, 32, 0.7);
      border: 1px solid ${borderSoft};
      font-size: 0.7rem;
    }
    .bubble {
      background: rgba(11, 19, 32, 0.55);
      border: 1px solid ${borderSoft};
      padding: 0.5rem 0.75rem;
      border-radius: 8px;
      color: var(--text-secondary);
      font-size: 0.86rem;
      line-height: 1.5;
      word-break: break-word;
      p, ul, ol { margin: 0.25rem 0; }
      strong { color: var(--text-primary); }
    }
    .actions-pill {
      margin-top: 4px;
      font-family: ${mono};
      font-size: 0.62rem;
      letter-spacing: 0.14em;
      color: #62d59a;
      background: rgba(98, 213, 154, 0.07);
      border: 1px solid rgba(98, 213, 154, 0.3);
      padding: 2px 7px;
      border-radius: 4px;
      display: inline-block;
    }
  }
  .chat-input-wrap {
    display: flex;
    gap: 6px;
    textarea {
      flex: 1;
      background: rgba(11, 19, 32, 0.6);
      border: 1px solid ${borderSoft};
      border-radius: 7px;
      color: var(--text-primary);
      font-family: inherit;
      padding: 8px 10px;
      font-size: 0.86rem;
      resize: none;
      min-height: 38px;
      max-height: 110px;
      &:focus { outline: none; border-color: rgba(0, 180, 216, 0.5); }
    }
    button {
      width: 38px; height: 38px;
      border-radius: 6px;
      border: 1px solid rgba(0, 180, 216, 0.35);
      background: rgba(0, 180, 216, 0.15);
      color: ${accent};
      cursor: pointer;
      display: flex; align-items: center; justify-content: center;
      &:disabled { opacity: 0.45; cursor: not-allowed; }
      &:hover:not(:disabled) { background: rgba(0, 180, 216, 0.3); }
    }
  }
`;

const SourcesPanel = styled.div`
  margin-top: 1.1rem;
  padding-top: 0.9rem;
  border-top: 1px dashed ${borderSoft};

  .head {
    font-family: ${mono};
    font-size: 0.62rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--text-disabled);
    margin-bottom: 0.55rem;
  }
  .list {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }
  .src {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 0.55rem;
    padding: 0.55rem 0.7rem;
    background: rgba(11, 19, 32, 0.45);
    border: 1px solid ${borderSoft};
    border-radius: 7px;
    text-decoration: none;
    color: var(--text-secondary);
    font-size: 0.84rem;
    transition: all 0.15s ease;
  }
  .src:hover {
    border-color: rgba(0, 180, 216, 0.4);
    background: rgba(0, 180, 216, 0.05);
    color: var(--text-primary);
  }
  .src.has-link { cursor: pointer; }
  .idx {
    font-family: ${mono};
    color: ${accent};
    font-size: 0.7rem;
    background: rgba(0, 180, 216, 0.1);
    border: 1px solid rgba(0, 180, 216, 0.35);
    padding: 2px 7px;
    border-radius: 4px;
    white-space: nowrap;
  }
  .ttl {
    line-height: 1.35;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
  .meta {
    display: flex;
    gap: 0.4rem;
    align-items: center;
    flex-wrap: wrap;
    justify-content: flex-end;
  }
  .chip {
    font-family: ${mono};
    font-size: 0.6rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--text-disabled);
    background: rgba(11, 19, 32, 0.6);
    border: 1px solid ${borderSoft};
    padding: 2px 6px;
    border-radius: 4px;
  }
`;

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'entities', label: 'Entidades' },
  { id: 'connections', label: 'Red' },
  { id: 'reports', label: 'Informes' },
  { id: 'theories', label: 'Teorías' },
  { id: 'notes', label: 'Notas' },
  { id: 'searches', label: 'Historial' },
];

function EntityIcon({ type }) {
  if (type === 'person') return <FaUserCircle />;
  if (type === 'company') return <FaIndustry />;
  if (type === 'organization') return <FaBuilding />;
  if (type === 'place') return <FaMapMarkerAlt />;
  if (type === 'event') return <FaCalendarAlt />;
  if (type === 'concept') return <FaLightbulb />;
  return <FaUserCircle />;
}

function NewEntityModal({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [type, setType] = useState('person');
  const [summary, setSummary] = useState('');
  const [importance, setImportance] = useState(50);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    if (!name.trim()) { setErr('Nombre requerido'); return; }
    setSaving(true);
    try {
      const e = await onCreated({ name: name.trim(), type, summary: summary.trim() || null, importance: Number(importance) });
      if (e) onClose();
    } catch (e) {
      setErr(friendlyError(e));
    } finally { setSaving(false); }
  };

  return (
    <ModalBackdrop onClick={onClose}>
      <ModalCard onClick={(e) => e.stopPropagation()}>
        <button type="button" className="close" onClick={onClose}><FaTimes /></button>
        <h2>Agregar entidad</h2>
        <label>Nombre</label>
        <TechInput autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej: Manuel Passaglia · Federico Chouhy · La Libertad Avanza SN · MoviPort S.A. · Puerto San Nicolás" />
        <label>Tipo</label>
        <select value={type} onChange={(e) => setType(e.target.value)}>
          <option value="person">Persona</option>
          <option value="company">Empresa</option>
          <option value="organization">Organización</option>
          <option value="place">Lugar</option>
          <option value="event">Evento</option>
          <option value="concept">Concepto</option>
        </select>
        <label>Resumen breve (opcional)</label>
        <textarea value={summary} onChange={(e) => setSummary(e.target.value)} />
        <label>Importancia (0-100): {importance}</label>
        <input type="range" min="0" max="100" value={importance} onChange={(e) => setImportance(e.target.value)} style={{ width: '100%' }} />
        {err && <ErrorBox style={{ marginTop: 12 }}>{err}</ErrorBox>}
        <div className="actions">
          <TechButton onClick={onClose} disabled={saving} style={{ background: 'transparent', color: 'var(--text-secondary)' }}>Cancelar</TechButton>
          <TechButton onClick={submit} disabled={saving || !name.trim()}>{saving ? '…' : 'Agregar'}</TechButton>
        </div>
      </ModalCard>
    </ModalBackdrop>
  );
}

function NewConnectionModal({ entities, onClose, onCreated }) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [kind, setKind] = useState('business');
  const [label, setLabel] = useState('');
  const [strength, setStrength] = useState(50);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    if (!from || !to || from === to) { setErr('Elegí dos entidades distintas'); return; }
    setSaving(true);
    try {
      await onCreated({
        from_entity_id: Number(from),
        to_entity_id: Number(to),
        kind, label: label.trim() || null, strength: Number(strength),
      });
      onClose();
    } catch (e) {
      setErr(friendlyError(e));
    } finally { setSaving(false); }
  };

  return (
    <ModalBackdrop onClick={onClose}>
      <ModalCard onClick={(e) => e.stopPropagation()}>
        <button type="button" className="close" onClick={onClose}><FaTimes /></button>
        <h2>Agregar conexión</h2>
        <label>Desde</label>
        <select value={from} onChange={(e) => setFrom(e.target.value)}>
          <option value="">— Elegí —</option>
          {entities.map((e) => <option key={e.id} value={e.id}>{e.name} ({e.type})</option>)}
        </select>
        <label>Hacia</label>
        <select value={to} onChange={(e) => setTo(e.target.value)}>
          <option value="">— Elegí —</option>
          {entities.map((e) => <option key={e.id} value={e.id}>{e.name} ({e.type})</option>)}
        </select>
        <label>Tipo</label>
        <select value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="business">Comercial</option>
          <option value="political">Política</option>
          <option value="family">Familiar</option>
          <option value="employment">Laboral</option>
          <option value="financial">Financiera</option>
          <option value="legal">Legal</option>
          <option value="social">Social</option>
          <option value="historical">Histórica</option>
          <option value="rivalry">Rivalidad</option>
          <option value="other">Otra</option>
        </select>
        <label>Etiqueta</label>
        <TechInput value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Ej: 'socio fundador', 'aliado de lista'" />
        <label>Fuerza (0-100): {strength}</label>
        <input type="range" min="0" max="100" value={strength} onChange={(e) => setStrength(e.target.value)} style={{ width: '100%' }} />
        {err && <ErrorBox style={{ marginTop: 12 }}>{err}</ErrorBox>}
        <div className="actions">
          <TechButton onClick={onClose} disabled={saving} style={{ background: 'transparent', color: 'var(--text-secondary)' }}>Cancelar</TechButton>
          <TechButton onClick={submit} disabled={saving || !from || !to}>{saving ? '…' : 'Agregar'}</TechButton>
        </div>
      </ModalCard>
    </ModalBackdrop>
  );
}

function NewReportModal({ slug, onClose, onCreated }) {
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState('overview');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    if (!title.trim() || !content.trim()) { setErr('Título y contenido requeridos'); return; }
    setSaving(true);
    try {
      await onCreated({ title: title.trim(), kind, content: content.trim() });
      onClose();
    } catch (e) {
      setErr(friendlyError(e));
    } finally { setSaving(false); }
  };

  return (
    <ModalBackdrop onClick={onClose}>
      <ModalCard onClick={(e) => e.stopPropagation()}>
        <button type="button" className="close" onClick={onClose}><FaTimes /></button>
        <h2>Nuevo informe</h2>
        <label>Título</label>
        <TechInput value={title} onChange={(e) => setTitle(e.target.value)} />
        <label>Tipo</label>
        <select value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="overview">Overview general</option>
          <option value="biography">Biografía</option>
          <option value="connections">Análisis de conexiones</option>
          <option value="timeline">Cronología</option>
          <option value="theory">Hipótesis / teoría</option>
          <option value="evidence_log">Bitácora de evidencia</option>
          <option value="custom">Otro</option>
        </select>
        <label>Contenido (markdown)</label>
        <textarea value={content} onChange={(e) => setContent(e.target.value)} style={{ minHeight: 240 }} />
        {err && <ErrorBox style={{ marginTop: 12 }}>{err}</ErrorBox>}
        <div className="actions">
          <TechButton onClick={onClose} disabled={saving} style={{ background: 'transparent', color: 'var(--text-secondary)' }}>Cancelar</TechButton>
          <TechButton onClick={submit} disabled={saving}>{saving ? '…' : 'Guardar'}</TechButton>
        </div>
      </ModalCard>
    </ModalBackdrop>
  );
}

function NewTheoryModal({ onClose, onCreated }) {
  const [title, setTitle] = useState('');
  const [statement, setStatement] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    if (!title.trim() || !statement.trim()) { setErr('Título y enunciado requeridos'); return; }
    setSaving(true);
    try {
      await onCreated({
        title: title.trim(),
        statement: statement.trim(),
        status: 'open',
        confidence: 50,
      });
      onClose();
    } catch (e) {
      setErr(friendlyError(e));
    } finally { setSaving(false); }
  };

  return (
    <ModalBackdrop onClick={onClose}>
      <ModalCard onClick={(e) => e.stopPropagation()}>
        <button type="button" className="close" onClick={onClose}><FaTimes /></button>
        <h2>Nueva hipótesis</h2>
        <label>Título corto</label>
        <TechInput
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Ej: Nepotismo en la SubSec. de Cultura"
        />
        <label>Enunciado completo</label>
        <textarea
          value={statement}
          onChange={(e) => setStatement(e.target.value)}
          style={{ minHeight: 120 }}
          placeholder="Ej: Manuel Passaglia concentra adjudicaciones de obra pública en empresas vinculadas a su familia."
        />
        <div style={{ marginTop: 8, fontSize: '0.78rem', color: 'var(--text-disabled)' }}>
          La hipótesis arranca con status <strong>open</strong> y confianza 50%. Después podés correr "Investigar a fondo" para que la IA la confronte con la evidencia.
        </div>
        {err && <ErrorBox style={{ marginTop: 12 }}>{err}</ErrorBox>}
        <div className="actions">
          <TechButton onClick={onClose} disabled={saving} style={{ background: 'transparent', color: 'var(--text-secondary)' }}>Cancelar</TechButton>
          <TechButton onClick={submit} disabled={saving || !title.trim() || !statement.trim()}>{saving ? '…' : 'Guardar'}</TechButton>
        </div>
      </ModalCard>
    </ModalBackdrop>
  );
}

function TheoryDetailModal({ slug, theoryId, onClose, onMutate }) {
  const [theory, setTheory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [investigating, setInvestigating] = useState(false);
  const [investigateErr, setInvestigateErr] = useState(null);
  const [cite, setCite] = useState(null);

  // Chat scoped a la teoría
  const [chatHistory, setChatHistory] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [chatErr, setChatErr] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const chatBodyRef = React.useRef(null);

  const reload = async () => {
    setLoading(true);
    try {
      const t = await getTheory(slug, theoryId);
      setTheory(t);
    } finally { setLoading(false); }
  };

  const loadChat = async () => {
    try {
      const m = await getTheoryChatHistory(slug, theoryId);
      setChatHistory(m);
    } catch (e) {
      console.warn('theory chat history err', e.message);
    }
  };

  useEffect(() => { reload(); loadChat(); /* eslint-disable-next-line */ }, [theoryId]);

  useEffect(() => {
    if (chatBodyRef.current) chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
  }, [chatHistory, chatSending]);

  const investigate = async () => {
    if (!theory) return;
    setInvestigating(true);
    setInvestigateErr(null);
    try {
      await runAgent(slug, 'theory_evaluator', {
        title: theory.title,
        statement: theory.statement,
        theory_id: theory.id,
        mode: 'deep',
      });
      await reload();
      if (onMutate) onMutate();
    } catch (e) {
      setInvestigateErr(friendlyError(e));
    } finally { setInvestigating(false); }
  };

  const sendChat = async (override) => {
    const q = (override ?? chatInput).trim();
    if (!q || chatSending) return;
    setChatErr(null);
    setChatHistory((h) => [...h, { role: 'user', content: q, created_at: new Date().toISOString() }]);
    setChatInput('');
    setChatSending(true);
    setChatOpen(true);
    try {
      const r = await sendTheoryChat(slug, theoryId, q);
      // Si el endpoint devolvió la teoría actualizada, refrescamos
      if (r?.theory) setTheory(r.theory);
      await loadChat();
      if (onMutate) onMutate();
    } catch (e) {
      setChatErr(friendlyError(e));
    } finally { setChatSending(false); }
  };

  const clearChat = async () => {
    if (!window.confirm('¿Borrar el historial del chat de esta teoría?')) return;
    await clearTheoryChat(slug, theoryId);
    setChatHistory([]);
  };

  const evidence = Array.isArray(theory?.evidence) ? theory.evidence : [];
  const argsFor = Array.isArray(theory?.arguments_for) ? theory.arguments_for : [];
  const argsAgainst = Array.isArray(theory?.arguments_against) ? theory.arguments_against : [];

  const statusColor = {
    supported: '#62d59a',
    refuted: 'var(--alert-error)',
    uncertain: 'var(--alert-warning, #f4a261)',
    open: 'var(--text-disabled)',
  }[theory?.status] || 'var(--text-disabled)';

  return (
    <ModalBackdrop onClick={onClose}>
      <ModalCard onClick={(e) => e.stopPropagation()} style={{ width: 'min(820px, 100%)' }}>
        <button type="button" className="close" onClick={onClose}><FaTimes /></button>
        {loading || !theory ? <Loading>Cargando…</Loading> : (
          <>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <FaLightbulb style={{ color: accent }} /> {theory.title}
            </h2>
            <div style={{ fontFamily: mono, fontSize: '0.65rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--text-disabled)', marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <span style={{ color: statusColor, border: `1px solid ${statusColor}`, padding: '2px 8px', borderRadius: 4 }}>
                {theory.status}
              </span>
              <span>confianza {theory.confidence}%</span>
              <span>{theory.generated_by}</span>
              <span>{new Date(theory.updated_at || theory.created_at).toLocaleString('es-AR')}</span>
            </div>

            <div style={{ background: 'rgba(11,19,32,0.5)', border: '1px solid ' + borderSoft, borderLeft: `2px solid ${accent}`, borderRadius: 6, padding: '10px 14px', marginBottom: 16, fontSize: '0.92rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              {theory.statement}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div>
                <div style={{ fontFamily: mono, fontSize: '0.62rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: '#62d59a', marginBottom: 6 }}>
                  Argumentos a favor ({argsFor.length})
                </div>
                {argsFor.length === 0 ? (
                  <div style={{ color: 'var(--text-disabled)', fontSize: '0.84rem' }}>—</div>
                ) : argsFor.map((a, i) => (
                  <div key={i} style={{ background: 'rgba(98,213,154,0.06)', border: '1px solid rgba(98,213,154,0.25)', borderRadius: 6, padding: '7px 10px', marginBottom: 6, fontSize: '0.85rem', lineHeight: 1.5 }}>
                    {a.punto || a.point || JSON.stringify(a)}
                    {Array.isArray(a.evidencia) && a.evidencia.length > 0 && (
                      <div style={{ marginTop: 4 }}>
                        {a.evidencia.map((n) => (
                          <button
                            key={n}
                            type="button"
                            onClick={() => setCite({ prefix: '', num: String(n) })}
                            style={{ background: 'rgba(0,180,216,0.13)', border: '1px solid rgba(0,180,216,0.38)', color: accent, padding: '1px 6px', borderRadius: 4, fontSize: '0.7rem', marginRight: 4, cursor: 'pointer', fontFamily: mono }}
                          >
                            [{n}]
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div>
                <div style={{ fontFamily: mono, fontSize: '0.62rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--alert-error)', marginBottom: 6 }}>
                  Argumentos en contra ({argsAgainst.length})
                </div>
                {argsAgainst.length === 0 ? (
                  <div style={{ color: 'var(--text-disabled)', fontSize: '0.84rem' }}>—</div>
                ) : argsAgainst.map((a, i) => (
                  <div key={i} style={{ background: 'rgba(239,71,111,0.06)', border: '1px solid rgba(239,71,111,0.25)', borderRadius: 6, padding: '7px 10px', marginBottom: 6, fontSize: '0.85rem', lineHeight: 1.5 }}>
                    {a.punto || a.point || JSON.stringify(a)}
                    {Array.isArray(a.evidencia) && a.evidencia.length > 0 && (
                      <div style={{ marginTop: 4 }}>
                        {a.evidencia.map((n) => (
                          <button
                            key={n}
                            type="button"
                            onClick={() => setCite({ prefix: '', num: String(n) })}
                            style={{ background: 'rgba(0,180,216,0.13)', border: '1px solid rgba(0,180,216,0.38)', color: accent, padding: '1px 6px', borderRadius: 4, fontSize: '0.7rem', marginRight: 4, cursor: 'pointer', fontFamily: mono }}
                          >
                            [{n}]
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {evidence.length > 0 && (
              <SourcesPanel>
                <div className="head">Evidencia consultada ({evidence.length})</div>
                <div className="list">
                  {evidence.map((s, i) => (
                    <a
                      key={s.idx || i}
                      href={s.url || '#'}
                      target={s.url ? '_blank' : undefined}
                      rel={s.url ? 'noreferrer' : undefined}
                      className={s.url ? 'src has-link' : 'src'}
                      onClick={(e) => {
                        if (!s.url) {
                          e.preventDefault();
                          setCite({ prefix: '', num: String(s.idx || i + 1).replace(/^N/, '') });
                        }
                      }}
                    >
                      <span className="idx">[{s.idx || i + 1}]</span>
                      <span className="ttl">{s.titulo || s.title || 'Sin título'}</span>
                      <span className="meta">
                        {s.fuente && <span className="chip">{s.fuente}</span>}
                        {(s.scope || s.scope_match) && <span className="chip">{s.scope || s.scope_match}</span>}
                        {s.url && <FaExternalLinkAlt style={{ fontSize: '0.62rem', marginLeft: 4 }} />}
                      </span>
                    </a>
                  ))}
                </div>
              </SourcesPanel>
            )}

            {investigateErr && <ErrorBox style={{ marginTop: 12 }}>{investigateErr}</ErrorBox>}

            <div className="actions" style={{ marginTop: 16 }}>
              <TechButton onClick={onClose} disabled={investigating} style={{ background: 'transparent', color: 'var(--text-secondary)' }}>Cerrar</TechButton>
              <TechButton onClick={investigate} disabled={investigating}>
                {investigating ? 'Investigando… (1-3 min)' : '▶ Investigar a fondo'}
              </TechButton>
              <TechButton
                onClick={() => setChatOpen((v) => !v)}
                style={{ background: 'rgba(0,180,216,0.1)', borderColor: 'rgba(0,180,216,0.4)' }}
              >
                <FaCommentDots style={{ marginRight: 6 }} />
                {chatOpen ? 'Ocultar chat' : `Chatear con IA${chatHistory.length ? ` (${chatHistory.length})` : ''}`}
              </TechButton>
            </div>
            <div style={{ marginTop: 6, fontSize: '0.74rem', color: 'var(--text-disabled)' }}>
              "Investigar a fondo" corre theory_evaluator en modo deep: amplía la búsqueda RAG, scrapea web sobre las entidades mencionadas, y reescribe argumentos + confidence. El chat permite ir agregando argumentos puntuales en vivo.
            </div>

            {chatOpen && (
              <TheoryChatPanel>
                <div className="chat-head">
                  <FaRobot style={{ color: accent }} />
                  <span className="title">Chat sobre esta hipótesis</span>
                  {chatHistory.length > 0 && (
                    <button type="button" className="clear" onClick={clearChat} title="Borrar historial">
                      <FaTrashAlt />
                    </button>
                  )}
                </div>
                <div className="chat-body" ref={chatBodyRef}>
                  {chatHistory.length === 0 && !chatSending && (
                    <div className="empty">
                      Charlá con la IA sobre esta hipótesis. Puede buscar en el corpus HCD,
                      scrapear web, lanzar entity_researcher, y agregar argumentos a favor/contra
                      en vivo. Probá:
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 10 }}>
                        {[
                          '¿Hay denuncias judiciales recientes contra Manuel Passaglia que respalden esta hipótesis?',
                          'Buscá si hay contratos sospechosos entre la Municipalidad y empresas familiares.',
                          'Refutá esta hipótesis con la evidencia disponible.',
                        ].map((s) => (
                          <button key={s} type="button" className="suggest" onClick={() => sendChat(s)}>
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {chatHistory.map((m) => (
                    <div key={m.id || m.created_at} className={`msg ${m.role}`}>
                      <div className="avatar">{m.role === 'user' ? <FaUser /> : <FaRobot />}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="bubble">
                          {m.role === 'assistant' ? (
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                          ) : m.content}
                        </div>
                        {Array.isArray(m.actions) && m.actions.length > 0 && (
                          <div className="actions-pill">
                            ✓ {m.actions.filter((a) => a.ok).map((a) => a.tool).join(', ')}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {chatSending && (
                    <div className="msg assistant">
                      <div className="avatar"><FaRobot /></div>
                      <div className="bubble" style={{ color: 'var(--text-disabled)' }}>investigando…</div>
                    </div>
                  )}
                  {chatErr && <ErrorBox>{chatErr}</ErrorBox>}
                </div>
                <form
                  className="chat-input-wrap"
                  onSubmit={(e) => { e.preventDefault(); sendChat(); }}
                >
                  <textarea
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendChat();
                      }
                    }}
                    placeholder="Preguntale algo sobre esta hipótesis…"
                    disabled={chatSending}
                    rows={1}
                  />
                  <button type="submit" disabled={chatSending || !chatInput.trim()} aria-label="Enviar">
                    <FaPaperPlane />
                  </button>
                </form>
              </TheoryChatPanel>
            )}

            <CitePopup
              cite={cite}
              indexed={evidence.map((s, i) => ({
                idx: s.idx || `N${i + 1}`,
                titulo: s.titulo || s.title,
                fuente: s.fuente || s.source,
                url: s.url,
                fecha: s.fecha || s.published_at,
                extracto: s.extracto || s.summary,
              }))}
              onClose={() => setCite(null)}
            />
          </>
        )}
      </ModalCard>
    </ModalBackdrop>
  );
}

function ReportViewModal({ slug, reportId, onClose }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cite, setCite] = useState(null);
  useEffect(() => {
    setLoading(true);
    getReport(slug, reportId).then(setReport).finally(() => setLoading(false));
  }, [slug, reportId]);

  const citations = Array.isArray(report?.citations) ? report.citations : [];
  const components = wrapWithCitations(setCite);

  return (
    <ModalBackdrop onClick={onClose}>
      <ModalCard onClick={(e) => e.stopPropagation()} style={{ width: 'min(880px, 100%)' }}>
        <button type="button" className="close" onClick={onClose}><FaTimes /></button>
        {loading ? <Loading>Cargando…</Loading> : (
          <>
            <h2>{report?.title || 'Informe'}</h2>
            <div style={{ fontFamily: mono, fontSize: '0.62rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--text-disabled)', marginBottom: 10 }}>
              {report?.kind} · {report?.generated_by} · {new Date(report?.created_at).toLocaleString('es-AR')}
              {citations.length > 0 && <span> · {citations.length} fuentes citadas</span>}
            </div>
            <div className="md">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
                {report?.content || ''}
              </ReactMarkdown>
            </div>
            {citations.length > 0 && (
              <SourcesPanel>
                <div className="head">Fuentes consultadas ({citations.length})</div>
                <div className="list">
                  {citations.map((s, i) => (
                    <a
                      key={s.idx || i}
                      href={s.url || '#'}
                      target={s.url ? '_blank' : undefined}
                      rel={s.url ? 'noreferrer' : undefined}
                      className={s.url ? 'src has-link' : 'src'}
                      onClick={(e) => {
                        if (!s.url) {
                          e.preventDefault();
                          setCite({ prefix: '', num: String(s.idx || '').replace(/^N/, '') });
                        }
                      }}
                    >
                      <span className="idx">[{s.idx}]</span>
                      <span className="ttl">{s.titulo || 'Fuente sin título'}</span>
                      <span className="meta">
                        {s.fuente && <span className="chip">{s.fuente}</span>}
                        {s.fecha && <span className="chip">{new Date(s.fecha).toLocaleDateString('es-AR')}</span>}
                        {s.url && <FaExternalLinkAlt style={{ fontSize: '0.62rem', marginLeft: 4 }} />}
                      </span>
                    </a>
                  ))}
                </div>
              </SourcesPanel>
            )}
          </>
        )}
        <CitePopup cite={cite} indexed={citations} onClose={() => setCite(null)} />
      </ModalCard>
    </ModalBackdrop>
  );
}

export default function InvestigationDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState('overview');
  const [showEntityModal, setShowEntityModal] = useState(false);
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showReportId, setShowReportId] = useState(null);
  const [showAgentLauncher, setShowAgentLauncher] = useState(false);
  const [showTheoryModal, setShowTheoryModal] = useState(false);
  const [showTheoryId, setShowTheoryId] = useState(null);
  const [noteText, setNoteText] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      setErr(null);
      const d = await getInvestigation(slug);
      setData(d);
    } catch (e) {
      setErr(friendlyError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  if (loading) return <TechShell><Loading>Cargando…</Loading></TechShell>;
  if (err) return <TechShell><BackLink to="/investigations"><FaArrowLeft /> Volver</BackLink><ErrorBox>{err}</ErrorBox></TechShell>;
  if (!data?.investigation) return <TechShell><ErrorBox>Investigación no encontrada</ErrorBox></TechShell>;

  const { investigation: inv, entities, connections, reports, theories, notes, searches } = data;

  const addEntity = async (payload) => {
    const e = await createEntity(slug, payload);
    await load();
    return e;
  };
  const removeEntity = async (id) => {
    if (!window.confirm('¿Eliminar entidad?')) return;
    await deleteEntity(slug, id);
    await load();
  };
  const addConnection = async (payload) => {
    await createConnection(slug, payload);
    await load();
  };
  const removeConnection = async (id) => {
    if (!window.confirm('¿Eliminar conexión?')) return;
    await deleteConnection(slug, id);
    await load();
  };
  const addReport = async (payload) => {
    await createReport(slug, payload);
    await load();
  };
  const addTheory = async (payload) => {
    await createTheory(slug, payload);
    await load();
  };
  const removeTheory = async (id) => {
    if (!window.confirm('¿Eliminar teoría?')) return;
    await deleteTheory(slug, id);
    await load();
  };
  const removeReport = async (id) => {
    if (!window.confirm('¿Eliminar informe?')) return;
    await deleteReport(slug, id);
    await load();
  };
  const addNote = async () => {
    if (!noteText.trim()) return;
    await createNote(slug, { content: noteText.trim() });
    setNoteText('');
    await load();
  };

  return (
    <TechShell>
      <BackLink to="/investigations"><FaArrowLeft /> Volver a investigaciones</BackLink>
      <TechPageHeader
        title={inv.title}
        subtitle={[
          inv.hypothesis,
          inv.region ? `📍 ${inv.region}` : null,
          inv.scope ? `alcance: ${inv.scope}` : null,
        ].filter(Boolean).join(' · ') || 'Sin hipótesis declarada'}
        dossier={`INV-${String(inv.id).padStart(4, '0')}`}
        actions={
          <TechButton onClick={() => setShowAgentLauncher(true)}>
            <FaRobot style={{ marginRight: 6 }} /> Lanzar agente IA
          </TechButton>
        }
      />

      <TechTabs tabs={TABS} active={tab} onChange={setTab} />

      {tab === 'overview' && (
        <AutopilotPanel
          slug={slug}
          summary={{
            entities: entities.length,
            connections: connections.length,
            reports: reports.length,
            theories: theories.length,
            notes: notes.length,
            searches: searches.length,
          }}
          entities={entities}
          onMutate={load}
          onNavigateTab={setTab}
        />
      )}

      {tab === 'entities' && (
        <TechSection
          title={`Entidades (${entities.length})`}
          action={<TechButton onClick={() => setShowEntityModal(true)}><FaPlus style={{ marginRight: 6 }} /> Agregar</TechButton>}
        >
          {entities.length === 0 ? <Empty>Ninguna entidad todavía.</Empty> : (
            <Grid>
              {entities.map((e) => (
                <EntityCard key={e.id}>
                  <div className="actions">
                    <button type="button" onClick={() => removeEntity(e.id)} title="Eliminar"><FaTrashAlt /></button>
                  </div>
                  <div className="top">
                    <div className="avatar"><EntityIcon type={e.type} /></div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="name">{e.name}</div>
                      <div className="type">{e.type} · importancia {e.importance}</div>
                    </div>
                  </div>
                  {e.summary && <div className="summary">{e.summary}</div>}
                </EntityCard>
              ))}
            </Grid>
          )}
          {showEntityModal && (
            <NewEntityModal
              onClose={() => setShowEntityModal(false)}
              onCreated={addEntity}
            />
          )}
        </TechSection>
      )}

      {tab === 'connections' && (
        <TechSection
          title={`Red de conexiones (${connections.length} edges · ${entities.length} nodos)`}
          action={
            <TechButton onClick={() => setShowConnectionModal(true)} disabled={entities.length < 2}>
              <FaPlus style={{ marginRight: 6 }} /> Agregar
            </TechButton>
          }
        >
          <div style={{ marginBottom: '1rem' }}>
            <EntityGraph entities={entities} connections={connections} />
          </div>
          {entities.length < 2 && <Empty>Necesitás al menos 2 entidades para crear conexiones.</Empty>}
          {connections.length === 0 && entities.length >= 2 && <Empty>Ninguna conexión todavía. Dispará el agente Connection Finder para que las detecte automáticamente.</Empty>}
          {connections.map((c) => (
            <ConnectionRow key={c.id}>
              <span className="from">{c.from_name}</span>
              <span className="arrow">→</span>
              <span className="to">{c.to_name}</span>
              <span className="kind">{c.kind}</span>
              {c.label && <span className="desc">{c.label}</span>}
              <button type="button" className="del" onClick={() => removeConnection(c.id)} title="Eliminar"><FaTrashAlt /></button>
            </ConnectionRow>
          ))}
          {showConnectionModal && (
            <NewConnectionModal
              entities={entities}
              onClose={() => setShowConnectionModal(false)}
              onCreated={addConnection}
            />
          )}
        </TechSection>
      )}

      {tab === 'reports' && (
        <TechSection
          title={`Informes (${reports.length})`}
          action={<TechButton onClick={() => setShowReportModal(true)}><FaPlus style={{ marginRight: 6 }} /> Nuevo informe</TechButton>}
        >
          {reports.length === 0 ? <Empty>Ningún informe todavía.</Empty> : reports.map((r) => (
            <ReportCard key={r.id} onClick={() => setShowReportId(r.id)}>
              <div className="title"><FaFileAlt style={{ marginRight: 8, color: accent }} />{r.title}</div>
              <div className="meta">{r.kind} · {r.generated_by} · {new Date(r.created_at).toLocaleString('es-AR')}</div>
              <div className="preview">{r.preview}</div>
              <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
                <button type="button" onClick={(e) => { e.stopPropagation(); removeReport(r.id); }}
                  style={{ background: 'transparent', border: '1px solid ' + borderSoft, color: 'var(--text-disabled)', padding: '4px 9px', borderRadius: 4, cursor: 'pointer', fontSize: '0.72rem' }}>
                  <FaTrashAlt /> Eliminar
                </button>
              </div>
            </ReportCard>
          ))}
          {showReportModal && <NewReportModal slug={slug} onClose={() => setShowReportModal(false)} onCreated={addReport} />}
          {showReportId && <ReportViewModal slug={slug} reportId={showReportId} onClose={() => setShowReportId(null)} />}
        </TechSection>
      )}

      {tab === 'theories' && (
        <TechSection
          title={`Teorías (${theories.length})`}
          action={<TechButton onClick={() => setShowTheoryModal(true)}><FaPlus style={{ marginRight: 6 }} /> Nueva hipótesis</TechButton>}
        >
          {theories.length === 0 ? (
            <Empty>
              Ninguna teoría todavía. La IA propone hipótesis automáticamente
              cuando corrés el autopilot (después de investigar entidades). También
              podés crear una manual con "Nueva hipótesis".
            </Empty>
          ) : theories.map((t) => {
            const statusColor = {
              supported: '#62d59a',
              refuted: 'var(--alert-error)',
              uncertain: 'var(--alert-warning, #f4a261)',
              open: 'var(--text-disabled)',
            }[t.status] || 'var(--text-disabled)';
            return (
              <ReportCard key={t.id} onClick={() => setShowTheoryId(t.id)}>
                <div className="title"><FaLightbulb style={{ marginRight: 8, color: accent }} />{t.title}</div>
                <div className="meta" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                  <span style={{ color: statusColor, border: `1px solid ${statusColor}`, padding: '2px 6px', borderRadius: 4 }}>
                    {t.status}
                  </span>
                  <span>confianza {t.confidence}%</span>
                  <span>· {Array.isArray(t.arguments_for) ? t.arguments_for.length : 0} a favor</span>
                  <span>· {Array.isArray(t.arguments_against) ? t.arguments_against.length : 0} en contra</span>
                  <span>· {t.generated_by}</span>
                </div>
                <div className="preview">{t.statement}</div>
                <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); removeTheory(t.id); }}
                    style={{ background: 'transparent', border: '1px solid ' + borderSoft, color: 'var(--text-disabled)', padding: '4px 9px', borderRadius: 4, cursor: 'pointer', fontSize: '0.72rem' }}
                  >
                    <FaTrashAlt /> Eliminar
                  </button>
                </div>
              </ReportCard>
            );
          })}
          {showTheoryModal && <NewTheoryModal onClose={() => setShowTheoryModal(false)} onCreated={addTheory} />}
          {showTheoryId && (
            <TheoryDetailModal
              slug={slug}
              theoryId={showTheoryId}
              onClose={() => setShowTheoryId(null)}
              onMutate={load}
            />
          )}
        </TechSection>
      )}

      {tab === 'notes' && (
        <TechSection title={`Notas (${notes.length})`}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <TechInput
              placeholder="Agregar nota…"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addNote(); }}
            />
            <TechButton onClick={addNote} disabled={!noteText.trim()}>Guardar</TechButton>
          </div>
          {notes.length === 0 ? <Empty>Sin notas.</Empty> : notes.map((n) => (
            <div key={n.id} style={{ padding: 10, background: panelDeep, border: '1px solid ' + borderSoft, borderRadius: 7, marginBottom: 7 }}>
              <div style={{ fontSize: '0.62rem', color: 'var(--text-disabled)', fontFamily: mono, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 4 }}>
                {new Date(n.created_at).toLocaleString('es-AR')}
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{n.content}</div>
            </div>
          ))}
        </TechSection>
      )}

      {tab === 'searches' && (
        <TechSection title={`Historial de búsquedas (${searches.length})`}>
          {searches.length === 0 ? <Empty>Sin búsquedas. Los agentes IA loguearán acá cada corrida.</Empty> : searches.map((s) => (
            <div key={s.id} style={{ padding: 10, background: panelDeep, border: '1px solid ' + borderSoft, borderRadius: 7, marginBottom: 7 }}>
              <div style={{ fontSize: '0.6rem', color: 'var(--text-disabled)', fontFamily: mono, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
                {s.agent} · {s.status} · {s.elapsed_ms ? s.elapsed_ms + 'ms' : '—'}
              </div>
              <pre style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: '6px 0 0', whiteSpace: 'pre-wrap' }}>
                {JSON.stringify(s.input, null, 2)}
              </pre>
              {s.error_message && <div style={{ color: 'var(--alert-error)', fontSize: '0.82rem', marginTop: 6 }}>{s.error_message}</div>}
            </div>
          ))}
        </TechSection>
      )}

      {showAgentLauncher && (
        <AgentLauncher
          slug={slug}
          onClose={() => setShowAgentLauncher(false)}
          onCompleted={() => { load(); }}
        />
      )}
    </TechShell>
  );
}
